/*
 * File: CustomTimeline.tsx
 * Project: a-a
 * File Created: Monday, 16th December 2024 12:40:30 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 10th December 2025 7:01:07 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import { Box, Divider, Skeleton, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import zeplinColor from '../../../theme/Color';
import CustomCard from '../CustomCard/CustomCard';
import CustomPopOver from '../CustomPopOver/CustomPopOver';
import { LightTooltip } from '../CustomTooltip/CustomTooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CustomAlert from '../CustomAlert/CustomAlert';
import style from './CustomTimeline.module.css';
import { useLocation } from 'react-router-dom';
const CustomTimeline = ({
  loading,
  subject,
  addedBy,
  closeDate,
  stageDetails,
  statusDetails,
  link,
  amountDetails,
  modifiedBy,
  oldStage,
  oldAmount,
  date,
  newDataTotal,
  index,
  source,
}: {
  loading: boolean;
  subject: JSX.Element | null;
  addedBy: JSX.Element | null;
  closeDate: JSX.Element | null;
  stageDetails: JSX.Element | null;
  statusDetails: JSX.Element | null;
  amountDetails: JSX.Element | null;
  modifiedBy: JSX.Element | null;
  oldStage: JSX.Element | null;
  oldAmount: JSX.Element | null;
  date: any;
  link: string;
  newDataTotal: number;
  index: number;
  source: string;
}) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const theme = useTheme();
  const [copy, setCopy] = useState('');
  const handleCopyLink = (val: any) => {
    navigator.clipboard.writeText(val);
    setCopy('Link Copied');
  };
  const handleClose = () => {
    setCopy('');
  };
  const now = new Date(); // Current time
  const eventDate = new Date(date); // Replace date with the actual date string
  let timeDifference = 0;
  let timeText = '';
  if (isNaN(eventDate.getTime())) {
    // Handle invalid date
    console.error('Invalid date:', date);
  } else {
    timeDifference = now.getTime() - eventDate.getTime(); // getTime() gives milliseconds
    console.log(timeDifference);
  }

  // Convert milliseconds to minutes and hours
  const minutesAgo = Math.floor(timeDifference / 1000 / 60); // Minutes
  const hoursAgo = Math.floor(timeDifference / 1000 / 60 / 60); // Hours

  // If it's less than an hour, show minutes; otherwise, show hours
  if (hoursAgo > 23) {
    timeText = eventDate?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // This enables the AM/PM format
    });
  } else if (hoursAgo > 0) {
    timeText = `${hoursAgo}h ago`;
  } else if (minutesAgo > 0) {
    timeText = `${minutesAgo}m ago`;
  } else {
    timeText = 'Just now';
  }
  const alertMessage = (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // backgroundColor: !error ? zeplinColor.Success : '',
          borderRadius: '60px',
          padding: '3px',
        }}
      >
        <img src='/assets/checkmark-green.png' width={24} height={24} />
      </div>
      <Typography variant='responsiveBody2' style={{ color: zeplinColor.Neutral1 }}>
        {copy}
      </Typography>
    </div>
  );
  return (
    <Box sx={{ display: 'flex', gap: { xs: '10px', xl: '16px' }, width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            borderRadius: '100px',
            border: `1px solid ${zeplinColor.Neutral4}`,
            padding: '20px',
            width: '26px',
            height: '26px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor:
              location?.pathname?.includes('huddle') || source === 'HUDDLE'
                ? zeplinColor.BasicBlack
                : zeplinColor.Neutral1,
            flex: 1,
          }}
        >
          {loading ? (
            <Skeleton
              variant='rounded'
              width={20}
              height={20}
              sx={{ color: zeplinColor.Neutral3, width: '100%' }}
            />
          ) : (
            <Box
              sx={{
                borderRadius: '8px',
                // border: `1px solid ${zeplinColor.Neutral5}`,
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: '25px', xl: '30px' },
                height: { xs: '25px', xl: '30px' },
                backgroundColor:
                  location?.pathname?.includes('huddle') || source === 'HUDDLE'
                    ? zeplinColor.BasicBlack
                    : zeplinColor.Neutral1,
              }}
            >
              {' '}
              {location?.pathname?.includes('huddle') || source === 'HUDDLE' ? (
                <Typography sx={{ fontSize: '8px', color: zeplinColor.Neutral1 }}>
                  Huddle
                </Typography>
              ) : (
                <img src={`/assets/salesforce.png`} className={style['salesforce-icon']} />
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ width: '2px', height: '100%', backgroundColor: zeplinColor.Neutral3 }}></Box>
      </Box>
      <CustomAlert
        open={copy ? true : false}
        handleClose={handleClose}
        message={alertMessage}
        error={false}
      />
      <CustomCard
        styles={{
          borderRadius: '14px',

          backgroundColor: index < newDataTotal ? 'rgba(73, 86, 244, 0.1)' : zeplinColor.Neutral3,
          padding: { xs: '10px', xl: '16px' },
          gap: { xs: '10px', xl: '16px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '1rem',
        }}
      >
        {loading ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                  // Optionally adjust other styles here
                }}
              />
              <Box sx={{ position: 'relative' }}>
                {/* <Button aria-describedby={id} onClick={handleClick}> */}
                <LightTooltip
                  title={
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      Options
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
                >
                  {/* <MoreVertIcon onClick={handleClick} aria-hidden='false' /> */}
                  <img
                    className={style['vertical-dots']}
                    src={'/assets/vertical-dots.svg'}
                    alt='options-icon'
                    onClick={handleClick}
                    aria-hidden='false'
                    // style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
                  />
                </LightTooltip>
                {/* </Button> */}
                <CustomPopOver
                  setAnchorEl={setAnchorEl}
                  anchorEl={anchorEl}
                  id={id}
                  sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: '10px', xl: '16px' },
                      padding: { xs: '10px', xl: '16px' },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={() => {}}>
                      <img src={'/assets/copy-link.svg'} className={style.icon} />
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        Copy Link
                      </Typography>
                    </Box>
                    <a
                      href={'#'}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ textDecoration: 'none' }}
                    >
                      <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}>
                        <img src={'/assets/view.svg'} className={style.icon} />
                        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                          View in salesforce
                        </Typography>
                      </Box>
                    </a>
                  </Box>
                </CustomPopOver>
              </Box>
            </Box>
            <CustomCard
              styles={{
                borderRadius: '8px',

                backgroundColor: zeplinColor.Neutral1,
                padding: '16px',
                display: 'flex',
                gap: '16px',
                // flex: 1,
                width: '50%',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', flex: 1 }}>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 1,
                    // Optionally adjust other styles here
                  }}
                />
              </Box>
              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
              />
              <Box sx={{ display: 'flex', flex: 1 }}>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 1,
                    // Optionally adjust other styles here
                  }}
                />
              </Box>
              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
              />
              <Box sx={{ display: 'flex', flex: 1 }}>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 1,
                    // Optionally adjust other styles here
                  }}
                />
              </Box>
            </CustomCard>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {subject}
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Typography
                  variant='responsiveBody3'
                  color={index < newDataTotal ? zeplinColor.Brand1 : zeplinColor.Neutral10}
                  // sx={{ width: '100%' }}
                >
                  {timeText}
                </Typography>
                {/* <Button aria-describedby={id} onClick={handleClick}> */}
                <LightTooltip
                  title={
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      Options
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
                >
                  {/* <MoreVertIcon onClick={handleClick} aria-hidden='false' /> */}
                  <img
                    className={style['vertical-dots']}
                    src={'/assets/vertical-dots.svg'}
                    alt='options-icon'
                    onClick={handleClick}
                    aria-hidden='false'
                    // style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
                  />
                </LightTooltip>
                {/* </Button> */}
                <CustomPopOver
                  setAnchorEl={setAnchorEl}
                  anchorEl={anchorEl}
                  id={id}
                  sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
                >
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px' }}
                  >
                    <Box
                      sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                      onClick={() => handleCopyLink(link)}
                    >
                      <img src={'/assets/copy-link.svg'} width={18} height={18} />
                      <Typography variant='body3' color={zeplinColor.BasicBlack}>
                        Copy Link
                      </Typography>
                    </Box>
                    <a
                      href={link}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ textDecoration: 'none' }}
                    >
                      <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}>
                        <img src={'/assets/view.svg'} width={18} height={18} />
                        <Typography variant='body3' color={zeplinColor.BasicBlack}>
                          View in salesforce
                        </Typography>
                      </Box>
                    </a>
                  </Box>
                </CustomPopOver>
              </Box>
            </Box>
            <CustomCard
              styles={{
                borderRadius: '8px',

                backgroundColor: zeplinColor.Neutral1,
                padding: '16px',
                display: 'flex',
                gap: '16px',
                // flex: 1,
                // width: '50%',
                alignItems: 'center',
              }}
            >
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Added By:</Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  {' '}
                  Ameanes
                </Typography>
              </Box> */}
              {addedBy}
              {modifiedBy ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Stage: </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  Determine budget
                </Typography>
              </Box> */}
              {modifiedBy}

              {stageDetails ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Stage: </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  Determine budget
                </Typography>
              </Box> */}
              {stageDetails}
              {statusDetails ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Stage: </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  Determine budget
                </Typography>
              </Box> */}
              {statusDetails}
              {oldStage ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Close date: </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  12 Oct, 2024
                </Typography>
              </Box> */}
              {oldStage}
              {amountDetails ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}

              {amountDetails}
              {oldAmount ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}
              {/* <Box sx={{ display: 'flex' }}>
                <Typography variant='body2'>Close date: </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  12 Oct, 2024
                </Typography>
              </Box> */}
              {oldAmount}
              {closeDate ? (
                <Divider
                  component='div'
                  sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
                />
              ) : (
                ''
              )}

              {closeDate}
            </CustomCard>
          </>
        )}
      </CustomCard>
    </Box>
  );
};

export default CustomTimeline;
